/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { Label, Dropdown as DefaultDropdown, TextField } from '@vr/lib';
import clearNotNumber from '@vr/devkit/utils/clearNotNumber';
import { Grid } from '@material-ui/core';
import apiLocal from '~/config/axios';
import { Button } from '~/elements';
import If from '~/components/If';
import {
  updateField,
  setInfoEndereco,
} from '~/store/modules/indicacao/actions';
import getSchemaValidation from '../../validators/IndicacaoDadosEstabelecimento';
import { loadPerguntasRequest } from '~/store/modules/perguntas/actions';
import estados from '~/config/estados';
import removerAcentuacao from '~/helpers/removerAcentuacao';
import { BreadcrumbsContext } from '~/providers/BreadcrumbsProvider';
import Icon from '~/elements/Icon';
import getEnv from '../../helpers/getEnv';

const Dropdown = styled(DefaultDropdown)`
  .select-label-container {
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray300} !important;
    margin-bottom: 2px;
    height: 40px;
  }
`;
const ButtonContainer = styled.div`
  margin-left: 15px;
  .MuiButton-root {
    border-color: #00aa13 !important;
    color: #00aa13 !important;

    &:hover {
      color: #fff !important;
      background: #008c15;
    }
  }
`;
const ContainerButton = styled(Grid)`
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
`;
const Form = styled.form`
  margin-top: 30px;

  fieldset {
    border: none;
  }
  legend {
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary}
    margin-bottom: 1rem;
  }

  .MuiGrid-root {
    label {
      color: ${({ theme }) => theme.colors.gray300} !important;
    }
  }
  input,
  label {
    color: ${({ theme }) => theme.colors.gray300} !important;
    font-size: 15px;
  }
  input {
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray300} !important;
  }
`;
const Row = styled.div`
  .select-label-container {
    margin-bottom: 10px;
    width: 100%;
  }
  input {
    margin-bottom: 10px;
  }
`;

const StyledLabel = styled(Label)`
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 10px;
`;

const ReadonlyContainer = styled.div`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.gray300};
  font-weight: 500;
  margin-top: 5px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  a.btn {
    margin-left: 40px;
    i {
      margin-right: 10px;
    }
  }
`;
const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.gray400};
  margin-left: 11px;
  margin-top: 20px;
`;

export default function FormIndicacao({ history }) {
  const dispatch = useDispatch();
  const formRef = useRef();

  const form = useSelector(state => state.indicacao);
  const tipoIndicacao = useSelector(state => state.indicacao.tipo);
  const categorias = useSelector(state => state.categorias);
  const [formErrors, setFormErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [cepIsValid, setCepIsValid] = useState(!!form.cep);
  const [cidades, setCidades] = useState([]);
  const [enableSearchCep, setEnableSearchCep] = useState(false);
  const breadcrumbs = useContext(BreadcrumbsContext);
  const btnStyle = useSelector(state => state?.issuer?.button?.primary?.style);

  function setBreadcrumb(icon, label) {
    breadcrumbs.setBreadcrumb({
      icon,
      label,
    });
  }

  useEffect(() => {
    setBreadcrumb('store', 'Indique um estabelecimento');
    return () => setBreadcrumb();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (tipoIndicacao === '') {
      history.push('/indique');
    }
  }, [tipoIndicacao]);

  const schema = useMemo(() => {
    return getSchemaValidation(tipoIndicacao);
  }, [tipoIndicacao]);

  const checkFormValid = useCallback(
    async (key, obj) => {
      let newErrors = { ...formErrors };
      try {
        await schema.validateAt(key, { [key]: obj });
        delete newErrors[key];
      } catch (err) {
        const { path, message } = err;
        const errorMsg = { [path]: message };
        newErrors = { ...formErrors, ...errorMsg };
      }

      if (!newErrors.cep && !cepIsValid && form.cep) {
        newErrors.cep = 'Insira um CEP válido';
      }

      const hasErrors = Object.keys(newErrors).length >= 1;
      const formValid = formRef.current
        ? formRef.current.checkValidity()
        : false;
      setIsValid(formValid && cepIsValid && !hasErrors);
      setFormErrors(newErrors);
    },
    [formErrors, formRef, cepIsValid],
  );

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      await schema.validate(form, {
        abortEarly: false,
        stripUnknown: true,
        context: {},
      });

      const status = String(form.status).toLowerCase() || 'inativo';

      dispatch(loadPerguntasRequest(status));
      history.push('/indique/perguntas');
    } catch (err) {
      const errors = {};
      // eslint-disable-next-line no-return-assign
      err.inner.forEach(er => (errors[er.path] = er.message));
      setFormErrors(errors);
    }
  }

  const handleOnChangeText = useCallback(
    e => {
      const { name, value, required } = e.target;

      if (required) {
        checkFormValid(name, value);
      }

      dispatch(updateField(name, value));
    },
    [checkFormValid, dispatch],
  );

  const handleChangeCategoria = useCallback(
    ({ label, value }) => {
      if (label && value) {
        dispatch(updateField('categoria', { label, value }));
        checkFormValid('categoria', value);
      } else {
        dispatch(updateField('categoria', ''));
        setIsValid(false);
        setFormErrors(currentState => ({
          ...currentState,
          categoria: 'Selecione uma categoria',
        }));
      }
    },
    [dispatch, checkFormValid],
  );

  const handleChangeEstado = useCallback(
    ({ value }) => {
      dispatch(updateField('estado', value));
      checkFormValid('estado', value);
      dispatch(updateField('cidade', ''));
    },
    [dispatch, checkFormValid],
  );

  const handleChangeCidade = useCallback(
    ({ value }) => {
      dispatch(updateField('cidade', value));
      checkFormValid('cidade', value);
    },
    [dispatch, checkFormValid],
  );

  const handleChangeCep = useCallback(
    e => {
      setEnableSearchCep(true);
      handleOnChangeText(e);
    },
    [handleOnChangeText],
  );

  useEffect(() => {
    const { CancelToken } = axios;
    const source = CancelToken.source();

    if (form.estado) {
      setCidades([]);
      const { cidade } = form;
      apiLocal
        .get(
          `${getEnv(
            'redecredenciada',
          )}/comum/cadastro/listar-municipios/estado`,
          {
            cancelToken: source.token,
            params: {
              uf: form.estado,
            },
          },
        )
        .then(
          ({ data }) => {
            setCidades(data);
            if (cidade) {
              dispatch(updateField('cidade', ''));
              setTimeout(() => dispatch(updateField('cidade', cidade)), 50);
            }
          },
          err => {
            if (axios.isCancel(err)) {
              return;
            }
            setFormErrors({
              ...formErrors,
              cidade: 'Não foi possível carregar as cidades',
            });
          },
        );
    }

    return () => source.cancel();
  }, [form.estado]);

  useEffect(() => {
    const { CancelToken } = axios;
    const source = CancelToken.source();

    if (form.cep.length === 9 && enableSearchCep) {
      const cep = clearNotNumber(form.cep);

      apiLocal
        .get(`${getEnv('redecredenciada')}/comum/cep/consultar/cep/${cep}`, {
          cancelToken: source.token,
        })
        .then(({ data }) => {
          const enderecoCep = {
            endereco: `${data.tipoLogradouro} ${data.logradouro}`.trim(),
            numero: data.numero === '0' ? '' : data.numero,
            complemento: data.complemento,
            bairro: data.bairro,
            cidade: removerAcentuacao(data.cidade).toUpperCase(),
            estado: data.uf,
          };

          dispatch(setInfoEndereco(enderecoCep));
          setCepIsValid(true);
          setFormErrors(state => {
            const errors = { ...state };
            delete errors.cep;
            return errors;
          });
        })
        .catch(({ response }) => {
          setCepIsValid(false);
          setIsValid(false);

          const messageErrorCep =
            response && response.data && response.data.message
              ? response.data.message
              : 'Insira um CEP válido';
          setFormErrors(state => ({ ...state, cep: messageErrorCep }));

          dispatch(
            setInfoEndereco({
              endereco: '',
              numero: '',
              complemento: '',
              bairro: '',
              cidade: '',
              estado: '',
            }),
          );
        });
    }

    return () => source.cancel();
  }, [form.cep, enableSearchCep]);

  return (
    <Form
      ref={formRef}
      onSubmit={handleSubmit}
      autoComplete="off"
      noValidate
      id="form-indicacao-dados"
    >
      <fieldset>
        <legend>Dados do estabelecimento</legend>
        <Row>
          {/* Exibição de CNPJ e Razão Social */}
          <If test={tipoIndicacao === 'cnpj'}>
            <Row>
              <Grid>
                <StyledLabel>CNPJ:</StyledLabel>
                <ReadonlyContainer>
                  {form.cnpj}
                  <ButtonContainer>
                    <Button
                      color={btnStyle}
                      size="medium"
                      variant="outlined"
                      label="Editar"
                      startIcon={<Icon icon={['far', 'pencil']} />}
                      href="#/indique"
                      id="btn-editar-cnpj"
                    />
                  </ButtonContainer>
                </ReadonlyContainer>
              </Grid>
            </Row>
            <Row>
              <Grid>
                {form.status ? (
                  <>
                    <StyledLabel>Razão Social:</StyledLabel>
                    <ReadonlyContainer>{form.razaoSocial}</ReadonlyContainer>
                  </>
                ) : (
                  <TextField
                    label="Razão Social"
                    name="razaoSocial"
                    value={form.razaoSocial}
                    onChange={handleOnChangeText}
                    messageError={formErrors.razaoSocial || ''}
                    required
                  />
                )}
              </Grid>
            </Row>
          </If>

          <If test={tipoIndicacao !== 'cnpj'}>
            <Row>
              <Grid item md={6}>
                <TextField
                  fullWidth
                  label="Nome do Estabelecimento"
                  name="nomeFantasia"
                  value={form.nomeFantasia}
                  onChange={handleOnChangeText}
                  messageError={formErrors.nomeFantasia || ''}
                  required
                />
              </Grid>
            </Row>
          </If>
          <Row>
            <Grid item md={6}>
              <TextField
                fullWidth
                type="tel"
                label="Telefone"
                name="telefone"
                value={form.telefone}
                onChange={handleOnChangeText}
                messageError={formErrors.telefone || ''}
                pattern="\([0-9]{2}\) [0-9]{4,5}-[0-9]{4}$"
                maskPhone
                required
              />
            </Grid>
          </Row>
          {/* Campo Nome Fantasia apenas para o tipo de busca CNPJ */}
          <If test={tipoIndicacao === 'cnpj'}>
            <Row>
              <Grid>
                <TextField
                  fullWidth
                  label="Nome Fantasia"
                  name="nomeFantasia"
                  value={form.nomeFantasia}
                  onChange={handleOnChangeText}
                  messageError={formErrors.nomeFantasia || ''}
                  required
                />
              </Grid>
            </Row>
          </If>
          <Row>
            <Grid item md={6}>
              <TextField
                fullWidth
                label="CEP"
                name="cep"
                value={form.cep}
                onChange={handleChangeCep}
                messageError={formErrors.cep}
                mask="99999-999"
                pattern="[0-9]{5}-[0-9]{3}"
                required
              />
            </Grid>
          </Row>

          {/* Campos comuns */}
          <Row>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Endereço"
                  name="endereco"
                  value={form.endereco}
                  onChange={handleOnChangeText}
                  messageError={formErrors.endereco || ''}
                  required
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Número"
                  name="numero"
                  value={form.numero}
                  onChange={handleOnChangeText}
                  messageError={formErrors.numero || ''}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Complemento"
                  name="complemento"
                  value={form.complemento}
                  onChange={handleOnChangeText}
                  messageError={formErrors.complemento || ''}
                />
              </Grid>
            </Grid>
          </Row>

          <Row>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Bairro"
                  name="bairro"
                  value={form.bairro}
                  onChange={handleOnChangeText}
                  messageError={formErrors.bairro || ''}
                  required
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Dropdown
                  label="UF"
                  name="estado"
                  value={form.estado}
                  onChange={handleChangeEstado}
                  messageError={formErrors.estado}
                  valid={!formErrors.estado && form.estado}
                  required
                >
                  {estados.map(estado => (
                    <Dropdown.Item key={estado.label} value={estado.key}>
                      {estado.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown>
              </Grid>
              <Grid item xs={12} md={5}>
                <Dropdown
                  label="Cidade"
                  name="cidade"
                  value={form.cidade}
                  onChange={handleChangeCidade}
                  messageError={formErrors.cidade}
                  valid={!formErrors.cidade && form.cidade}
                  required
                >
                  {cidades.map(cidade => (
                    <Dropdown.Item key={cidade.nome} value={cidade.nome}>
                      {cidade.nome}
                    </Dropdown.Item>
                  ))}
                </Dropdown>
              </Grid>
            </Grid>
          </Row>
        </Row>
      </fieldset>
      <fieldset>
        <legend>
          <Subtitle>
            1 - Selecione abaixo a categoria do estabelecimento
          </Subtitle>
        </legend>
        <Row>
          <Grid>
            <Dropdown
              name="categoria"
              label="Categoria do estabelecimento"
              onChange={handleChangeCategoria}
              value={form.categoria.value}
              messageError={formErrors.categoria}
              valid={!formErrors.categoria && form.categoria.value}
              required
            >
              {categorias.map(categoria => (
                <Dropdown.Item key={categoria.label} value={categoria.key}>
                  {categoria.label}
                </Dropdown.Item>
              ))}
            </Dropdown>
          </Grid>
        </Row>
      </fieldset>
      <Row>
        <ContainerButton>
          <Button
            label="Continuar"
            type="submit"
            variant="contained"
            disabled={!isValid}
          />
        </ContainerButton>
      </Row>
    </Form>
  );
}

FormIndicacao.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};
